import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CalendarDays, CalendarRange, Star, FileText, Images, BedDouble, Tag,
  LogOut, MessageSquare, Percent, ShieldCheck, Sparkles, Ban, Menu, Home, X, FileDown, Fingerprint, Wallet, Rss, CreditCard, UserCog, ClipboardList, Package, Banknote, Gauge, Inbox,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Overview from "./admin/Overview";
import Bookings from "./admin/Bookings";
import IdentityVerification from "./admin/IdentityVerification";
import Finance from "./admin/Finance";
import CalendarIntegration from "./admin/CalendarIntegration";
import PaymentMethods from "./admin/PaymentMethods";
import StaffManagement from "./admin/StaffManagement";
import WorksheetAdmin from "./admin/WorksheetAdmin";
import InventoryAdmin from "./admin/InventoryAdmin";
import PayrollAdmin from "./admin/PayrollAdmin";
import HousekeepingDashboard from "./admin/HousekeepingDashboard";
import ReorderInbox from "./admin/ReorderInbox";
import UnifiedCalendar from "./admin/UnifiedCalendar";
import CalendarChannels from "./admin/CalendarChannels";
import ReviewsAdmin from "./admin/ReviewsAdmin";
import ContentAdmin from "./admin/ContentAdmin";
import MediaAdmin from "./admin/MediaAdmin";
import ImagesAdmin from "./admin/ImagesAdmin";
import RoomsAdmin from "./admin/RoomsAdmin";
import RatesAdmin from "./admin/RatesAdmin";
import ExperiencesAdmin from "./admin/ExperiencesAdmin";
import PromotionsAdmin from "./admin/PromotionsAdmin";
import MessagesAdmin from "./admin/MessagesAdmin";
import TeamAdmin from "./admin/TeamAdmin";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "bookings", label: "Bookings", icon: CalendarDays },
  { id: "reservations", label: "Unified Calendar", icon: CalendarRange },
  { id: "identity", label: "Identity Verification", icon: Fingerprint, sysOnly: true },
  { id: "housekeeping", label: "Housekeeping", icon: Gauge, sysOnly: true },
  { id: "staff", label: "Staff Management", icon: UserCog, sysOnly: true },
  { id: "worksheets", label: "Cleaning Worksheets", icon: ClipboardList, sysOnly: true },
  { id: "inventory", label: "Inventory", icon: Package, sysOnly: true },
  { id: "reorders", label: "Reorder Inbox", icon: Inbox, sysOnly: true },
  { id: "payroll", label: "Payroll & Payslips", icon: Banknote, sysOnly: true },
  { id: "calendar", label: "Calendar & Channels", icon: Ban },
  { id: "calendar-integration", label: "Calendar Integration", icon: Rss, sysOnly: true },
  { id: "reviews", label: "Reviews", icon: Star },
  { id: "content", label: "Content", icon: FileText },
  { id: "media", label: "Media", icon: Images },
  { id: "images", label: "All Images", icon: Images, sysOnly: true },
  { id: "rooms", label: "Rooms & Rates", icon: BedDouble },
  { id: "rates", label: "Seasonal Rates", icon: Percent },
  { id: "experiences", label: "Experiences", icon: Sparkles },
  { id: "promotions", label: "Promotions", icon: Tag },
  { id: "finance", label: "Finance", icon: Wallet, sysOnly: true },
  { id: "payments", label: "Payment Methods", icon: CreditCard, sysOnly: true },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "team", label: "Team & Access", icon: ShieldCheck, sysOnly: true },
];

const ADMIN_ALLOWED = ["overview", "bookings", "reservations", "messages"];
const FACILITIES_ALLOWED = ["housekeeping", "staff", "worksheets", "inventory", "reorders", "payroll"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isSys = user?.role === "system_admin";
  const isFac = user?.role === "facilities_admin";
  const canClean = isSys || isFac;
  const allowed = isSys ? null : isFac ? FACILITIES_ALLOWED : ADMIN_ALLOWED;
  const visibleTabs = TABS.filter((t) => (allowed === null ? true : allowed.includes(t.id)));
  const [tab, setTab] = useState(isSys ? "overview" : isFac ? "housekeeping" : "bookings");
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);
  const pick = (id) => { setTab(id); setMenuOpen(false); };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-cream lg:flex">
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between bg-forest text-cream px-4 py-3">
        <button onClick={() => setMenuOpen(true)} data-testid="admin-menu-open" aria-label="Open menu"><Menu size={22} /></button>
        <span className="font-serif text-lg">Rose Air <span className="text-gold">Admin</span></span>
        <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-home-link-mobile" aria-label="Open website"><Home size={20} /></a>
      </div>
      {menuOpen && <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={closeMenu} data-testid="admin-menu-overlay" />}
      <aside className={`w-60 bg-forest text-cream/80 flex flex-col fixed h-full z-50 transform transition-transform duration-300 lg:translate-x-0 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-6 border-b border-white/10 flex items-start justify-between">
          <div>
            <div className="font-serif text-2xl text-cream">Rose Air <span className="text-gold">Admin</span></div>
            <div className="text-xs text-cream/50 mt-1">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-widest text-gold mt-1">{isSys ? "System Admin" : isFac ? "Facilities · Housekeeping" : "Admin · Bookings"}</div>
          </div>
          <button onClick={closeMenu} className="lg:hidden text-cream/70" data-testid="admin-menu-close" aria-label="Close menu"><X size={20} /></button>
        </div>
        <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-home-link" className="mx-3 mt-3 flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm bg-white/5 hover:bg-white/10"><Home size={17} /> View website</a>
        {isSys && <a href="/owner" target="_blank" rel="noopener noreferrer" data-testid="admin-owner-portal-link" className="mx-3 mt-2 flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm bg-gold/15 text-gold hover:bg-gold/25"><FileDown size={17} /> Owner Portal · Statements</a>}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {visibleTabs.map((t) => (
            <button key={t.id} onClick={() => pick(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${tab === t.id ? "bg-rose text-white" : "hover:bg-white/5"}`}>
              <t.icon size={17} /> {t.label}
            </button>
          ))}
        </nav>
        <button onClick={doLogout} data-testid="admin-logout" className="m-3 flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm hover:bg-white/5"><LogOut size={17} /> Sign out</button>
      </aside>

      <main className="flex-1 lg:ml-60 p-5 lg:p-8">
        <h1 className="font-serif text-3xl text-forest mb-6 capitalize">{TABS.find((t) => t.id === tab)?.label}</h1>
        {tab === "overview" && <Overview />}
        {tab === "bookings" && <Bookings />}
        {tab === "identity" && isSys && <IdentityVerification />}
        {tab === "housekeeping" && canClean && <HousekeepingDashboard onGoTo={setTab} />}
        {tab === "staff" && canClean && <StaffManagement />}
        {tab === "worksheets" && canClean && <WorksheetAdmin />}
        {tab === "inventory" && canClean && <InventoryAdmin />}
        {tab === "reorders" && canClean && <ReorderInbox />}
        {tab === "payroll" && canClean && <PayrollAdmin />}
        {tab === "reservations" && <UnifiedCalendar />}
        {tab === "calendar" && isSys && <CalendarChannels />}
        {tab === "calendar-integration" && isSys && <CalendarIntegration />}
        {tab === "reviews" && isSys && <ReviewsAdmin />}
        {tab === "content" && isSys && <ContentAdmin />}
        {tab === "media" && isSys && <MediaAdmin />}
        {tab === "images" && isSys && <ImagesAdmin />}
        {tab === "rooms" && isSys && <RoomsAdmin />}
        {tab === "rates" && isSys && <RatesAdmin />}
        {tab === "experiences" && isSys && <ExperiencesAdmin />}
        {tab === "promotions" && isSys && <PromotionsAdmin />}
        {tab === "finance" && isSys && <Finance />}
        {tab === "payments" && isSys && <PaymentMethods />}
        {tab === "messages" && <MessagesAdmin />}
        {tab === "team" && isSys && <TeamAdmin />}
      </main>
    </div>
  );
}
